import { Injectable } from '@angular/core';
import { AppHttpService } from 'src/app/shared/services';

/**
 * Forms (control_form) are project-scoped in the schema, so an organization
 * "shares" a form by keeping a copy of it in each project. This service backs
 * the project Settings > Forms screen: list a project's forms, browse every form
 * in the organization, and copy one of them into the project.
 */
@Injectable({ providedIn: 'root' })
export class ControlFormService {

  constructor(
    private appHttpService: AppHttpService,
  ) { }

  /**
   * Returns the forms configured on a project
   * @param projectId Project.id
   * @returns {Promise<any[]>}
   */
  getProjectForms(projectId: string): Promise<any[]> {
    return this.appHttpService
      .getService(`/v2/project/${projectId}/control-forms`, {})
      .toPromise()
      .then((resp: any) => (Array.isArray(resp?.data) ? resp.data : []));
  }

  /**
   * Returns every form across the organization's projects.
   *
   * `unique` (the default) collapses a form that exists in several projects down
   * to the copy from the most recently created project — the others come back on
   * `other_projects`.
   * @param organizationId Organization.id
   * @param opts { unique, projectId } projectId flags forms the project already has
   * @returns {Promise<any[]>}
   */
  getOrganizationForms(
    organizationId: string,
    opts: { unique?: boolean; projectId?: string } = {},
  ): Promise<any[]> {
    const qp: any = { unique: opts.unique === false ? false : true };
    if (opts.projectId) {
      qp.project = opts.projectId;
    }
    return this.appHttpService
      .getService(`/v2/organization/${organizationId}/control-forms`, qp)
      .toPromise()
      .then((resp: any) => (Array.isArray(resp?.data) ? resp.data : []));
  }

  /**
   * Copies a form from another project into this project. The copy is
   * independent — later edits to either form do not propagate.
   * @param projectId Project.id to assign the form to
   * @param formId ControlForm.id being copied
   * @returns {Promise<any>} the new form
   */
  assignToProject(projectId: string, formId: string): Promise<any> {
    return this.appHttpService
      .postService(`/v2/project/${projectId}/control-form/${formId}/copy`, {}, {})
      .toPromise()
      .then((resp: any) => resp?.data);
  }
}
